<template>
  <div>
    <div
      class="pa-3 grey lighten-4 d-flex flex-wrap"
      style="gap: 8px;"
    >
      <v-text-field
        v-model="filters.q"
        label="Search ref / customer"
        prepend-inner-icon="mdi-magnify"
        dense
        outlined
        clearable
        hide-details
        style="min-width: 220px; max-width: 300px;"
        @input="onFilterChangeDebounced"
      />
      <v-text-field
        v-model.number="filters.amount_min"
        label="Amount min"
        type="number"
        dense
        outlined
        clearable
        hide-details
        style="max-width: 140px;"
        @input="onFilterChangeDebounced"
      />
      <v-text-field
        v-model.number="filters.amount_max"
        label="Amount max"
        type="number"
        dense
        outlined
        clearable
        hide-details
        style="max-width: 140px;"
        @input="onFilterChangeDebounced"
      />
      <date-picker
        v-model="filters.date_from"
        type="date"
        format="YYYY-MM-DD"
        value-type="format"
        placeholder="Date from"
        :input-class="'mx-input dense'"
        style="max-width: 160px;"
        @change="onFilterChange"
      />
      <date-picker
        v-model="filters.date_to"
        type="date"
        format="YYYY-MM-DD"
        value-type="format"
        placeholder="Date to"
        :input-class="'mx-input dense'"
        style="max-width: 160px;"
        @change="onFilterChange"
      />
      <v-select
        v-if="!isMatchedTab"
        v-model="filters.match_status"
        :items="matchStatusOptions"
        label="Match status"
        dense
        outlined
        hide-details
        style="max-width: 200px;"
        @change="onFilterChange"
      />
      <v-select
        v-if="isMatchedTab"
        v-model="filters.bank"
        :items="bankOptions"
        label="Bank"
        dense
        outlined
        clearable
        hide-details
        style="max-width: 160px;"
        @change="onFilterChange"
      />
    </div>

    <v-data-table
      :headers="headers"
      :items="rows"
      :loading="loading"
      :server-items-length="total"
      :options.sync="tableOptions"
      :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
      :no-data-text="emptyText"
      item-key="id"
    >
      <template #item.bank="{ item }">
        <v-chip x-small>
          {{ item.bank }}
        </v-chip>
      </template>
      <template #item.statement_date="{ item }">
        {{ item.statement_date }}
      </template>
      <template #item.amount="{ item }">
        {{ formatAmount(item.amount) }}
      </template>
      <template #item.amount_delta="{ item }">
        <span :class="deltaClass(item.amount_delta)">
          {{ formatDelta(item.amount_delta) }}
        </span>
      </template>
      <template #item.match_status="{ item }">
        <v-chip
          x-small
          :color="matchStatusColor(item.match_status)"
          text-color="white"
        >
          {{ humanize(item.match_status) }}
        </v-chip>
      </template>
      <template #item.customer_hint="{ item }">
        <div>{{ item.customer_name || '—' }}</div>
        <div
          v-if="item.customer_phone"
          class="text-caption grey--text"
        >
          {{ item.customer_phone }}
        </div>
      </template>
      <template #item.linked_bill="{ item }">
        <span v-if="item.bills && item.bills.length">BILL-{{ item.bills[0].id }}</span>
        <span v-else>—</span>
      </template>
      <template #item.matched_at="{ item }">
        {{ item.approved_at ? formatDateTime(item.approved_at) : '—' }}
      </template>
      <template #item.matched_by="{ item }">
        <v-chip
          v-if="item.approved_by_user"
          x-small
        >
          {{ item.approved_by_user.name }}
        </v-chip>
        <v-chip
          v-else
          x-small
          color="grey lighten-2"
        >
          auto
        </v-chip>
      </template>
      <template #item.action="{ item }">
        <v-btn
          v-if="!readOnly && !isMatchedTab"
          x-small
          depressed
          color="primary"
          @click="$emit('link-bill', item)"
        >
          Link to bill
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import { getRecords } from '@/store/apis/bulkReconciliation';

const MATCH_STATUS_OPTIONS = [
  { value: 'unmatched', text: 'All unmatched' },
  { value: 'not_in_system', text: 'Not in system' },
  { value: 'amount_mismatch', text: 'Amount mismatch' },
  { value: 'missed_payment', text: 'Missed payment' },
  { value: 'expired', text: 'Expired' },
];

const MATCH_STATUS_COLORS = {
  matched: 'success',
  not_in_system: 'red',
  amount_mismatch: 'orange',
  missed_payment: 'deep-orange',
  expired: 'grey',
  paid_not_in_statement: 'purple',
};

export default {
  name: 'BulkResultTabBody',
  components: { DatePicker },
  props: {
    uuid: { type: String, required: true },
    tabKey: { type: String, required: true }, // 'bcel' | 'ldb' | 'matched'
    readOnly: { type: Boolean, default: false },
    refetchKey: { type: Number, default: 0 },
  },
  data() {
    return {
      rows: [],
      total: 0,
      loading: false,
      filters: this.defaultFilters(),
      tableOptions: { page: 1, itemsPerPage: 50 },
      filterDebounce: null,
      matchStatusOptions: MATCH_STATUS_OPTIONS,
      bankOptions: [
        { value: null, text: 'Any' },
        { value: 'BCEL', text: 'BCEL' },
        { value: 'LDB', text: 'LDB' },
      ],
    };
  },
  computed: {
    isMatchedTab() {
      return this.tabKey === 'matched';
    },
    headers() {
      if (this.isMatchedTab) {
        return [
          { text: 'Bank', value: 'bank', sortable: false },
          { text: 'Date', value: 'statement_date' },
          { text: 'Ref', value: 'transaction_ref', sortable: false },
          { text: 'Bill', value: 'linked_bill', sortable: false },
          { text: 'Customer', value: 'customer_hint', sortable: false },
          { text: 'Amount', value: 'amount', sortable: false },
          { text: 'Δ', value: 'amount_delta', sortable: false },
          { text: 'Matched by', value: 'matched_by', sortable: false },
          { text: 'Matched at', value: 'matched_at', sortable: false },
        ];
      }
      return [
        { text: 'Date', value: 'statement_date' },
        { text: 'Ref', value: 'transaction_ref', sortable: false },
        { text: 'Customer hint', value: 'customer_hint', sortable: false },
        { text: 'Amount', value: 'amount', sortable: false },
        { text: 'Status', value: 'match_status', sortable: false },
        {
          text: '', value: 'action', sortable: false, width: '120px',
        },
      ];
    },
    emptyText() {
      if (this.loading) return 'Loading…';
      if (this.isMatchedTab) return 'No matched rows in this batch.';
      return `No unmatched ${this.tabKey.toUpperCase()} rows in this batch.`;
    },
  },
  watch: {
    refetchKey() { this.fetch(); },
    tableOptions: {
      deep: true,
      handler() { this.fetch(); },
    },
  },
  created() {
    this.fetch();
  },
  beforeDestroy() {
    if (this.filterDebounce) clearTimeout(this.filterDebounce);
  },
  methods: {
    defaultFilters() {
      const base = {
        q: '',
        amount_min: null,
        amount_max: null,
        date_from: null,
        date_to: null,
      };
      if (this.tabKey === 'matched') {
        return { ...base, match_status: 'matched', bank: null };
      }
      return { ...base, match_status: 'unmatched', resolution_status: 'pending' };
    },
    onFilterChange() {
      this.tableOptions.page = 1;
      this.fetch();
    },
    onFilterChangeDebounced() {
      if (this.filterDebounce) clearTimeout(this.filterDebounce);
      this.filterDebounce = setTimeout(() => {
        this.tableOptions.page = 1;
        this.fetch();
      }, 300);
    },
    formatAmount(n) {
      if (n === null || n === undefined) return '—';
      return Number(n).toLocaleString();
    },
    formatDelta(n) {
      if (n === null || n === undefined) return '—';
      const sign = n > 0 ? '+' : '';
      return `${sign}${Number(n).toLocaleString()}`;
    },
    deltaClass(n) {
      if (n === null || n === undefined) return 'grey--text';
      if (Number(n) === 0) return 'success--text';
      return 'orange--text text--darken-2';
    },
    formatDateTime(iso) {
      return iso ? moment(iso).format('YYYY-MM-DD HH:mm') : '—';
    },
    humanize(status) {
      if (!status) return '—';
      return status.replace(/_/g, ' ');
    },
    matchStatusColor(status) {
      return MATCH_STATUS_COLORS[status] || 'grey';
    },
    bankParam() {
      if (this.tabKey === 'bcel') return 'BCEL';
      if (this.tabKey === 'ldb') return 'LDB';
      return this.filters.bank || null;
    },
    async fetch() {
      this.loading = true;
      try {
        const params = {
          page: this.tableOptions.page,
          per_page: this.tableOptions.itemsPerPage,
        };
        const bank = this.bankParam();
        if (bank) params.bank = bank;
        if (this.filters.match_status) params.match_status = this.filters.match_status;
        if (this.filters.resolution_status) params.resolution_status = this.filters.resolution_status;
        if (this.filters.q) params.q = this.filters.q;
        if (this.filters.amount_min !== null && this.filters.amount_min !== '') params.amount_min = this.filters.amount_min;
        if (this.filters.amount_max !== null && this.filters.amount_max !== '') params.amount_max = this.filters.amount_max;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;

        const res = await getRecords(this.uuid, params);
        this.rows = res.data || res.items || [];
        this.total = res.total || res.meta?.total || this.rows.length;
        this.$emit('count-changed', this.total);
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to load records.';
        this.$store.commit('Toast_State', { value: true, color: 'error', msg });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
